import React, { useEffect, useState } from 'react';
import type { Props as ReactModalProps } from 'react-modal';

let chunk: Promise<typeof import('react-modal')> | undefined;
const loadModal = () =>
    (chunk ??= import('react-modal').catch((error) => {
        chunk = undefined;
        throw error;
    }));

/**
 * react-modal, loaded only in the browser the first time a modal opens.
 * react-modal renders nothing while closed (and nothing on the server), so
 * skipping it until `isOpen` keeps the markup identical.
 */
const LazyModal: React.FC<ReactModalProps> = (props) => {
    const [ReactModal, setModal] = useState<
        typeof import('react-modal')['default'] | null
    >(null);
    const [failed, setFailed] = useState(false);
    const [attempt, setAttempt] = useState(0);

    useEffect(() => {
        if (!props.isOpen || ReactModal) return;
        let active = true;
        setFailed(false);
        loadModal().then(
            ({ default: Component }) => {
                if (active) setModal(() => Component);
            },
            () => {
                if (active) setFailed(true);
            }
        );
        return () => {
            active = false;
        };
    }, [props.isOpen, ReactModal, attempt]);

    if (!props.isOpen) return null;

    if (failed) {
        return (
            <div
                role="alert"
                onKeyDown={(event) => {
                    if (event.key === 'Escape') props.onRequestClose?.(event);
                }}
            >
                <p>
                    Unable to load {props.contentLabel || 'this dialog'}. Please
                    try again.
                </p>
                <button
                    type="button"
                    onClick={() => setAttempt((value) => value + 1)}
                >
                    Retry
                </button>
                <button
                    type="button"
                    onClick={(event) => props.onRequestClose?.(event)}
                >
                    Close
                </button>
            </div>
        );
    }

    if (!ReactModal) return null;

    const appElement =
        props.appElement ??
        (props.ariaHideApp === false || typeof document === 'undefined'
            ? undefined
            : document.getElementById('___gatsby') || document.body);

    return <ReactModal {...props} appElement={appElement} />;
};

/** Fetches react-modal ahead of time, for modals that must open instantly. */
export const preloadModal = () => {
    // Preloading is optional; opening the dialog retries a failed download.
    void loadModal().catch(() => {});
};

export default LazyModal;
